import moment from 'moment-timezone'

import {
  DATE_FORMAT,
  DATETIME_FORMAT,
  TIME_FORMAT,
  daysOfTheWeek
} from '@/lib/constants'

/**
 * Formats a subreddit to include the r/ prefix.
 * @param {string} subredditName
 */
export function formatSubreddit(subredditName) {
  if (subredditName.startsWith('/r/') || subredditName.startsWith('r/')) {
    return subredditName
  } else {
    return `r/${subredditName}`
  }
}

/**
 * Formats a subreddit to include the u/ prefix.
 */
export function formatUsername(username) {
  if (username.startsWith('/u/') || username.startsWith('u/')) {
    return username
  } else {
    return `u/${username}`
  }
}

export function formatDateTime(date) {
  return moment(date).format(DATETIME_FORMAT)
}

export function formatDate(date, format = DATE_FORMAT) {
  return moment(date).format(format)
}

export function formatTime(date) {
  return moment(date).format('h:mm a')
}

export function formatHour(hoursInDay) {
  let hour = hoursInDay
  let amPM = 'am'
  if (hoursInDay >= 12) {
    amPM = 'pm'
    if (hoursInDay !== 12) {
      hour = hoursInDay - 12
    }
  } else if (hoursInDay === 0) {
    hour = 12
  }
  return `${hour}:00 ${amPM}`
}

export function formatNumber(value) {
  const formatter = Intl.NumberFormat('en', { notation: 'compact' })
  return formatter.format(value).toLowerCase()
}

export function getMinutesBetweenDates(startDate, endDate) {
  const diff = endDate.getTime() - startDate.getTime()
  return Math.floor(diff / 60000)
}

export function dayOfTheWeekFromPython(dayIndex) {
  let day = null
  if (dayIndex === 6) {
    day = 0
  } else {
    day = dayIndex + 1
  }
  return daysOfTheWeek[day]
}

export function ensureMaxCharacters(string, max) {
  if (string.length > max) {
    return string.substring(0, max - 3) + '...'
  } else {
    return string
  }
}

export function parseQSArray(qsArray) {
  if (typeof qsArray === 'string') {
    return qsArray.split(',')
  } else if (Array.isArray(qsArray)) {
    return qsArray
  } else {
    return []
  }
}

export function titleCase(string) {
  return string[0].toUpperCase() + string.slice(1).toLowerCase()
}

export function getRandomMinuteInterval() {
  let minutes = Math.floor(Math.random() * 55)
  return (minutes = Math.round(minutes / 2) * 2)
}

export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function isDeviceMobile() {
  return window.matchMedia('only screen and (max-width: 768px)').matches
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getBrowserTimezone() {
  return moment.tz.guess() || 'UTC'
}

export function convertToTimezone(value, fromTz, toTz) {
  // We get the timezone offset from the given date so that seasonal changes like daylight savings are handled.
  const offset =
    moment(value).tz(fromTz).utcOffset() - moment(value).tz(toTz).utcOffset()
  const adjustedValue = new Date(value.getTime() - offset * 60000)
  return adjustedValue
}

export function ensureHttp(link) {
  if (link && typeof link === 'string' && !link.startsWith('http')) {
    if (link.startsWith('//')) {
      return 'https:' + link
    } else {
      return 'https://' + link
    }
  } else {
    return link
  }
}

export function extractDomain(urlString) {
  try {
    let url = new URL(urlString)
    return url.hostname
  } catch (e) {
    return urlString
  }
}

export function formatDueDate(dueDate) {
  const isToday = moment().diff(dueDate, 'days') === 0

  if (isToday) {
    return `today at ${moment(dueDate).format(TIME_FORMAT)}`
  }

  return formatDate(dueDate)
}

export function isValidUrl(url) {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

export function hasSpintax(text) {
  // We created our own `hasSpintax` function because we don't want to
  // consider brackets with a single option as Spintax.
  // This is the expected behavior:
  //   hasSpintax('{a}') => false
  //   hasSpintax('{a|b}') => true
  //   hasSpintax('{a|}') => true
  const spintaxRegex = /{[^{}]*\|[^{}\\]*}/
  return spintaxRegex.test(text)
}

export function pluralize(amount, word) {
  return amount > 1 || amount == 0 ? `${word}s` : word
}

/**
 * Convert a boolean string or number to boolean.
 * 'false', 0 -> false
 * 'true', 1 -> true
 */
export function toBoolean(value) {
  if (typeof value === 'boolean') {
    return value
  }
  if (!value) {
    return false
  }
  return ['1', 'true', 'yes', 'y'].includes(value.toLowerCase())
}

export function moveUpSubmissionTree(target, submissions) {
  const submissionTree = []
  const buildSubmissionTree = (submission) => {
    if (submission) {
      submissionTree.push(submission)
      submissions
        .filter((sub) => sub.crosspostFrom?.$id === submission.$id)
        .forEach((sub) => buildSubmissionTree(sub))
    }
  }
  const findPreviousSibling = (submission) => {
    const siblings = submissions.filter(
      (sub) => sub.crosspostFrom?.$id === submission.crosspostFrom?.$id
    )
    const index = siblings.findIndex((sub) => sub.$id === submission.$id)
    return siblings[index - 1]
  }

  // Find the submission and its descendants.
  buildSubmissionTree(target)

  const previousSibling = findPreviousSibling(target)

  if (previousSibling) {
    const submissionIndex = submissions.indexOf(target)
    // Remove the submission tree from its current position.
    submissions.splice(submissionIndex, submissionTree.length)
    // Insert the submission tree before its previous sibling.
    const moveToIndex = submissions.indexOf(previousSibling)
    submissions.splice(moveToIndex, 0, ...submissionTree)
  }

  return submissions
}

export function moveDownSubmissionTree(target, submissions) {
  const findNextSibling = (submission) => {
    const siblings = submissions.filter(
      (sub) => sub.crosspostFrom?.$id === submission.crosspostFrom?.$id
    )
    const index = siblings.findIndex((sub) => sub.$id === submission.$id)
    return siblings[index + 1]
  }

  const nextSibling = findNextSibling(target)
  if (!nextSibling) {
    return submissions
  }
  // Instead of moving down the target, we move its next sibling up.
  return moveUpSubmissionTree(nextSibling, submissions)
}
